/**
 * React hooks for the atom store
 *
 * The store lives outside React (store.swap / store.deref). useSyncExternalStore
 * bridges it in: store.addWatch notifies registered listeners on every swap,
 * React re-reads getState (or a selector) and re-renders when the snapshot changes
 *
 * Prefer useStoreSelector in leaf containers so unrelated store updates do not
 * re-render the whole tree. useStoreState is for components that need many fields
 */
import { useSyncExternalStore } from 'react';

import { getState, store } from '@campaign/domain/store';
import { State } from '@campaign/types';

const listeners = new Set<() => void>();

store.addWatch('react-state', () => {
  listeners.forEach((listener) => listener());
});

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useStoreState(): State {
  return useSyncExternalStore(subscribe, getState, getState);
}

export function useStoreSelector<T>(selector: (state: State) => T): T {
  return useSyncExternalStore(
    subscribe,
    () => selector(getState()),
    () => selector(getState())
  );
}
